// src/pages/dashboard/DashboardPage.tsx - Main dashboard page component

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { fetchJournalEntries } from '../../store/slices/journalSlice';
import { fetchRelapseIncidents } from '../../store/slices/relapseSlice';
import { fetchProfile } from '../../store/slices/profileSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { entries, isLoading: journalLoading } = useSelector((state: RootState) => state.journal);
  const { incidents, isLoading: relapseLoading } = useSelector((state: RootState) => state.relapse);
  const { profile, isLoading: profileLoading } = useSelector((state: RootState) => state.profile);
  
  const [streakCount, setStreakCount] = useState(0);
  const [lastJournalDate, setLastJournalDate] = useState<Date | null>(null);
  
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchJournalEntries(currentUser.uid));
      dispatch(fetchRelapseIncidents(currentUser.uid));
      dispatch(fetchProfile(currentUser.uid));
    }
  }, [currentUser, dispatch]);
  
  useEffect(() => {
    if (entries.length > 0) {
      // Calculate streak
      let streak = 0;
      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      
      // Sort entries by timestamp in descending order
      const sortedEntries = [...entries].sort((a, b) => b.timestamp - a.timestamp);
      
      // Set last journal date
      setLastJournalDate(new Date(sortedEntries[0].timestamp));
      
      // Check if there's an entry for today
      const todayEntry = sortedEntries.find(entry => {
        const entryDate = new Date(entry.timestamp);
        entryDate.setHours(0, 0, 0, 0);
        return entryDate.getTime() === currentDate.getTime();
      });
      
      if (todayEntry) {
        streak = 1;
        
        // Check for consecutive previous days
        let checkDate = new Date(currentDate);
        checkDate.setDate(checkDate.getDate() - 1);
        
        let consecutiveDays = true;
        
        while (consecutiveDays) {
          const hasEntryForDate = sortedEntries.some(entry => {
            const entryDate = new Date(entry.timestamp);
            entryDate.setHours(0, 0, 0, 0);
            return entryDate.getTime() === checkDate.getTime();
          });
          
          if (hasEntryForDate) {
            streak++;
            checkDate.setDate(checkDate.getDate() - 1);
          } else {
            consecutiveDays = false;
          }
        }
      }
      
      setStreakCount(streak);
    }
  }, [entries]);
  
  const handleNewJournalEntry = () => {
    navigate('/journal/entry');
  };
  
  const handleViewJournal = () => {
    navigate('/journal');
  };
  
  const handleNewRelapseEntry = () => {
    navigate('/relapse/add');
  };
  
  const handleViewRelapse = () => {
    navigate('/relapse');
  };
  
  const handleViewSettings = () => {
    navigate('/settings');
  };
  
  const isLoading = journalLoading || relapseLoading || profileLoading;
  
  if (isLoading && !entries.length && !incidents.length && !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Dashboard
        </Typography>
        
        <IconButton 
          aria-label="settings"
          onClick={handleViewSettings}
          sx={{ 
            backgroundColor: 'background.paper',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Streak Card */}
        <Box sx={{ width: '100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Streak
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h3" component="div" fontWeight="bold" sx={{ mr: 2 }}>
                  {streakCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {streakCount === 1 ? 'day' : 'days'}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                {lastJournalDate 
                  ? `Last entry: ${lastJournalDate.toLocaleDateString()}`
                  : 'No entries yet'}
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<AddIcon />}
                onClick={handleNewJournalEntry}
              >
                New Entry
              </Button>
            </CardActions>
          </Card>
        </Box>
        
        {/* Journal Summary */}
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Journal Entries
            </Typography>
            
            {entries.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" align="center">
                  No journal entries yet
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={handleNewJournalEntry}
                  >
                    Create First Entry
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <List>
                  {entries.slice(0, 3).map((entry) => (
                    <ListItem
                      key={entry.id}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="view"
                          onClick={() => navigate(`/journal/entry/${entry.id}`)}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={new Date(entry.timestamp).toLocaleDateString()}
                        secondary={entry.content.length > 60 
                          ? `${entry.content.substring(0, 60)}...` 
                          : entry.content}
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    onClick={handleViewJournal}
                  >
                    View All
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={handleNewJournalEntry}
                  >
                    New Entry
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Box>
        
        {/* Relapse Tracker */}
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Relapse Tracking
            </Typography>
            
            {incidents.length === 0 ? (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" align="center">
                  No relapse incidents tracked yet
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={handleNewRelapseEntry}
                  >
                    Record First Incident
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <List>
                  {incidents.slice(0, 3).map((incident) => (
                    <ListItem
                      key={incident.id}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="edit"
                          onClick={() => navigate(`/relapse/add/${incident.id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <WarningIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${incident.type} - ${new Date(incident.timestamp).toLocaleDateString()}`}
                        secondary={incident.description.length > 60 
                          ? `${incident.description.substring(0, 60)}...` 
                          : incident.description}
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    onClick={handleViewRelapse}
                  >
                    View All
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={handleNewRelapseEntry}
                  >
                    Record Incident
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Box>
        
        {/* Profile Summary */}
        <Box sx={{ width: '100%' }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Inner Ops Profile
            </Typography>
            
            {!profile ? (
              <CircularProgress />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 30%', minWidth: '250px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Core Values
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {profile.coreValues.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No core values defined
                      </Typography>
                    ) : (
                      profile.coreValues.map((value, index) => (
                        <Typography key={index} variant="body2">
                          • {value}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ flex: '1 1 30%', minWidth: '250px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Pain Points
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {profile.painPoints.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No pain points defined
                      </Typography>
                    ) : (
                      profile.painPoints.map((point, index) => (
                        <Typography key={index} variant="body2">
                          • {point}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ flex: '1 1 30%', minWidth: '250px' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Life Themes
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {profile.lifeThemes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No life themes defined
                      </Typography>
                    ) : (
                      profile.lifeThemes.map((theme, index) => (
                        <Typography key={index} variant="body2">
                          • {theme}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default DashboardPage;
